import { PlusOutlined } from "@ant-design/icons";
import {
  TableProps,
  Button,
  Card,
  ConfigProvider,
  Flex,
  Table,
  Tooltip,
  Badge,
  Tabs,
} from "antd";
import CardSectionTitle from "components/CardSectionTitle.component";
import BuyPackageModal from "components/modals/BuyPackageModal.component";
import { getAllCustomerPackagesByCustomerId } from "hooks/queries/customerpackage";
import { useTabParam } from "hooks/useTabParam";
import { modalButtonsTheme } from "components/modals/ModalChrome";
import { StatusBadge } from "pages/schedule/component/ScheduleStatusBadge";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { th, tnum } from "shared/components/TableText";
import { PAGE_SIZE } from "shared/constants";
import PATHS from "shared/constants/paths";
import { translate } from "shared/languages";
import { CustomerPackage, InactiveReason, User } from "shared/types";
import { PackageSystemKind, UserType } from "shared/types/enum";
import {
  getActualRemainingCredits,
  getExpiryDateBadgeStatus,
  INACTIVE_REASON_LABELS,
  INACTIVE_REASON_TONE,
  isUserDeactivated,
  packageActivation,
  REWARD_LABEL,
  SHARED_LABEL,
} from "shared/utils";
import { formatDate, tzDate } from "shared/utils/dayjs";

const CustomerPackagePage = ({
  customer,
  isMembership = false,
}: {
  customer: User;
  isMembership?: boolean;
}) => {
  const customerId = customer.id;
  const navigate = useNavigate();
  // Both instances on the customer detail page (Paket tab "2" and Membership
  // tab "4") share ?sub=, mirroring how they previously shared defaultSubTab.
  const { activeKey, onChange } = useTabParam("active", "sub", [
    "active",
    "expired",
  ]);
  // A deactivated client (per-studio membership) can't be sold new packages.
  const isDeactivated = isUserDeactivated(customer, UserType.Customer);
  const { data: customerPackages, isLoading } =
    getAllCustomerPackagesByCustomerId(customerId);
  const activePackages = (customerPackages?.active ?? []).filter(
    (pkg) => pkg.package_deal.unlimited === isMembership
  );
  const expiredPackages = (customerPackages?.expired ?? []).filter(
    (pkg) => pkg.package_deal.unlimited === isMembership
  );

  const [isModalOpen, setIsModalOpen] = useState(false);

  const columns = (
    showActive: boolean
  ): TableProps<CustomerPackage>["columns"] => {
    return [
      {
        title: th(translate("label.purchase_date")),
        dataIndex: "purchase_date",
        key: "purchase_date",
        sorter: (a, b) => tzDate(a.purchase_date).diff(b.purchase_date),
        defaultSortOrder: "descend", // DEFAULT but not fixed sorting
        render: (_, { purchase_date }) => (
          <span style={tnum}>{formatDate(purchase_date)}</span>
        ),
      },
      {
        title: th(translate("label.package_name")),
        dataIndex: "name",
        render: (_, record) => {
          const { package_deal } = record;
          return (
            <Tooltip title={package_deal.class_type.map((pk) => pk.name).join(", ")}>
              <Flex align="center" gap={8} wrap="wrap">
                <span style={{ fontWeight: 600 }}>{package_deal.name}</span>
                {(package_deal.system_kind === PackageSystemKind.LoyaltyReward ||
                  package_deal.system_kind ===
                    PackageSystemKind.ReferralReward) && (
                  <StatusBadge
                    tone="purple"
                    label={REWARD_LABEL}
                    dot={false}
                  />
                )}
                {/* One badge for both directions (lent to this client, or
                    owned and lent out): the badge answers "is this shared",
                    and a single consistent label reads faster in a list than
                    two differently-worded ones. Who it is shared with, and
                    whose credits it spends, is named on the package detail
                    page rather than crowded into the row. */}
                {(record.is_shared_with_me ||
                  (record.shared_with?.length ?? 0) > 0) && (
                  <StatusBadge
                    tone="progress"
                    label={SHARED_LABEL}
                    dot={false}
                  />
                )}
              </Flex>
            </Tooltip>
          );
        },
      },

      // Onhold/remaining-credit columns are dropped on the Membership tab:
      // unlimited packages skip onhold tracking (always 0) and always render
      // "Tidak Terbatas" here, so neither column carries any information.
      ...(!isMembership
        ? [
            {
              title: th(translate("label.onhold_session")),
              dataIndex: "onhold_credits",
              key: "onhold_credits",
              render: (value: number) => <span style={tnum}>{value}</span>,
              sorter: (a: CustomerPackage, b: CustomerPackage) =>
                a.onhold_credits - b.onhold_credits,
            },
            {
              title: th(translate("label.available_session")),
              dataIndex: "sessions",
              key: "sessions",
              render: (_: unknown, customerPackage: CustomerPackage) => (
                <span style={tnum}>
                  {getActualRemainingCredits(customerPackage)} /{" "}
                  {customerPackage.total_credits}
                </span>
              ),
              sorter: (a: CustomerPackage, b: CustomerPackage) =>
                getActualRemainingCredits(a) - getActualRemainingCredits(b),
            },
          ]
        : []),
      {
        title: th(translate("label.expiry_date")),
        dataIndex: "expiry_date",
        key: "expiry_date",
        render: (_, customerPackage) => {
          const { expiry_date } = customerPackage;
          // expiry_date is nullable (a package that never expires). tzDate(null)
          // yields an invalid dayjs, which rendered the literal string
          // "Invalid Date" behind a green dot, because getExpiryDateBadgeStatus
          // falls through to "success" for a date it cannot compare.
          // A first-use package whose clock has not started has no expiry
          // yet (the stored date is only the auto-start ceiling).
          if (!expiry_date || packageActivation(customerPackage).unactivated) return <>-</>;
          const exp = tzDate(expiry_date).startOf("day");
          return <Badge status={getExpiryDateBadgeStatus(exp)} text={formatDate(exp)} />;
        },
        // Nulls sort last, matching the server's `nulls: "last"` ordering on
        // the same field. Comparing against a null produced NaN, which leaves
        // the sort order undefined.
        sorter: (a, b) => {
          if (!a.expiry_date && !b.expiry_date) return 0;
          if (!a.expiry_date) return 1;
          if (!b.expiry_date) return -1;
          return tzDate(a.expiry_date).diff(tzDate(b.expiry_date));
        },
      },
      ...(!showActive
        ? [
            {
              title: th(translate("label.inactive_reason")),
              dataIndex: "inactive_reason",
              key: "inactive_reason",
              render: (
                _: unknown,
                { inactive_reason }: Pick<CustomerPackage, "inactive_reason">
              ) => {
                return (
                  inactive_reason && (
                    <StatusBadge
                      tone={INACTIVE_REASON_TONE[inactive_reason as InactiveReason] ?? "neutral"}
                      label={INACTIVE_REASON_LABELS[inactive_reason as InactiveReason] ?? inactive_reason}
                    />
                  )
                );
              },
            },
          ]
        : []),
    ];
  };

  const renderPackageTable = (
    packages: CustomerPackage[],
    showActive: boolean
  ) => (
    <Table
      showSorterTooltip={false}
      rowKey="id"
      size={"middle"}
      dataSource={packages}
      columns={columns(showActive)}
      loading={isLoading}
      pagination={{
        pageSize: PAGE_SIZE,
        showSizeChanger: false,
      }}
      onRow={(record) => {
        return {
          onClick: () => navigate(`${PATHS.CUSTOMER_PACKAGE}/${record.id}`),
        };
      }}
    />
  );

  const tabItems = [
    {
      key: "active",
      label: `${translate("label.active_packages")} (${activePackages.length})`,
      children: renderPackageTable(activePackages, true),
    },
    {
      key: "expired",
      label: `${translate("label.expired_packages")}  (${
        expiredPackages.length
      })`,
      children: renderPackageTable(expiredPackages, false),
    },
  ];

  return (
    <>
      {/* Modal-buttons theme: navy Beli + ink Batal inside the modal. */}
      <ConfigProvider theme={modalButtonsTheme}>
        <BuyPackageModal
          customerId={customerId}
          open={isModalOpen}
          onCancel={() => setIsModalOpen(!isModalOpen)}
          isMembership={isMembership}
        />
      </ConfigProvider>
      <Card>
        <Flex vertical gap={18}>
          <Flex justify="space-between" align="flex-start" gap={16}>
            <CardSectionTitle
              title={translate(
                isMembership ? "title.customer_memberships" : "title.customer_packages"
              )}
              subtitle={translate(
                isMembership ? "subtitle.customer_memberships" : "subtitle.customer_packages"
              )}
            />
            {/* Button system: navy primary CTA. */}
            <ConfigProvider theme={modalButtonsTheme}>
              <Button
                onClick={() => {
                  setIsModalOpen(!isModalOpen);
                }}
                type="primary"
                icon={<PlusOutlined />}
                disabled={isDeactivated}
              >
                {translate("button.buy")} {translate(isMembership ? "membership" : "package")}
              </Button>
            </ConfigProvider>
          </Flex>
          <Tabs activeKey={activeKey} onChange={onChange} items={tabItems} />
        </Flex>
      </Card>
    </>
  );
};

export default CustomerPackagePage;
